import React, {useCallback, useEffect, useMemo, useState} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Icon from 'brandComponents/Icon';
import CelebritiesPopup from 'brandComponents/popups/CelebritiesPopup/CelebritiesPopup';
import {ICelebritiesPopupPropsData} from 'brandComponents/popups/CelebritiesPopup/ICelebritiesPopup';

import {ICelebritiesWrapperProps} from './ICelebritiesWrapper';

import styles from './CelebritiesWrapper.module.scss';

const CelebritiesWrapper = ({body}: ICelebritiesWrapperProps) => {
	const {managerRepository} = useGeneralProvider();
	const [popupData, setPopupData] = useState<ICelebritiesPopupPropsData>(null);
	const [lastTileClicked, setLastTileClicked] = useState<string>(null);

	const createCelebrityData = useCallback((children: any, title: any): ICelebritiesPopupPropsData | null => (
		children?.length
			? {
				title,
				imageUrl: children[0]?.props?.image?.props?.url || '',
				cta: {
					link: children[2]?.props?.targetUrl || children[2]?.props?.targetPage?.props.slug || '',
					title: children[2]?.props?.title || '',
				}
			}
			: null
	), []);

	const resetLastTileClicked = useCallback(() => setLastTileClicked(null), []);

	const handleOnClick = useCallback((selected_celebrity: ICelebritiesPopupPropsData | null, key: string) => {
		const isDesktop = ViewportMgr.query('>=x-large');

		if (isDesktop || lastTileClicked === key) {
			setPopupData(selected_celebrity);
			managerRepository.getPopupsMgr().openPopup('celebrities');
			resetLastTileClicked();
		} else {
			setLastTileClicked(key);
		}
	}, [lastTileClicked]);

	const handleOnCancel = useCallback(() => {
		managerRepository.getPopupsMgr().closePopup('celebrities');
		setPopupData(null);
	}, []);

	const onMouseHover = useCallback((key) => {
		const isDesktop = ViewportMgr.query('>=x-large');

		if (isDesktop) {
			setLastTileClicked(key);
		}
	}, []);

	const celebritiesElements = useMemo(() => {
		return body.map((item, i) => {
			const {children, SubtitlesCollection} = item?.props;
			const title = SubtitlesCollection?.reactElements[0];
			const popupData = createCelebrityData(children, title);
			const key = `${popupData?.cta?.title}-${i}`;

			return (
				<div
					key={key}
					className={cn(
						styles['module-celebrities'],
						{
							'hover': lastTileClicked === key
						}
					)}
					onClick={(e) => {
						e.stopPropagation();
						handleOnClick(popupData, key);
					}}
					onMouseEnter={() => onMouseHover(key)}
					onMouseLeave={resetLastTileClicked}
				>
					<Icon name="plus" />
					{item}
					{title && <div className={styles['module-celebrities--title']}>{title}</div>}
				</div>
			);
		});
	}, [body, handleOnClick]);

	useEffect(() => {
		document.addEventListener('click', resetLastTileClicked);

		return () => {
			document.removeEventListener('click', resetLastTileClicked);
		};
	}, []);

	return (
		<div className={styles['module-celebrities-wrapper']}>
			{celebritiesElements}
			<CelebritiesPopup data={popupData as ICelebritiesPopupPropsData} handleOnCancel={handleOnCancel} />
		</div>
	);
};

export default CelebritiesWrapper;
